import { useCallback, useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import classNames from './DeleteStudentsPopup.module.scss';
import ChooseReason from './ChooseReason';
import Successes from './Successes';
import PartialSuccesses from './PartialSuccesses';
import Failure from './Failure';
import BulkProgress from '@/components/school-management/BulkAction/BulkProgress/BulkProgress';
import { StudentReportItem } from '@/school-management/types/response';
import AppModal from '@/components/AppModal/AppModal';
import {
  BulkOperationResponseData,
  BulkOperationState,
  MoveStudentsBulkOperationItem,
} from '@/school-management/types/services/bulk';
import { bulkService } from '@/school-management/services/bulk';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';

type ViewType = 'choose_Reason' | 'successes' | 'partial_successes' | 'failure' | 'progress';

interface DeleteStudentsPopupProps {
  show?: boolean;
  students: StudentReportItem[];
  onClose: (moveedStudents: StudentReportItem[]) => void;
}

const DeleteStudentsPopup = ({ show, students, onClose }: DeleteStudentsPopupProps) => {
  const { user } = useAppSelector(authSelector);
  const [view, setView] = useState<ViewType>('choose_Reason');
  const [reason, setReason] = useState<string>();
  const [operationId, setOperationId] = useState<string>('');
  const [bulkOperation, setBulkOperation] = useState<BulkOperationResponseData>();

  useEffect(() => {
    if (show) {
      setView('choose_Reason');
      setReason(undefined);
      setOperationId('');
      setBulkOperation(undefined);
    }
  }, [show]);

  const closePopup = useCallback(() => {
    const studentsToProcess = students || [];

    if (view === 'successes') {
      onClose(studentsToProcess);
      return;
    }

    if (view === 'partial_successes') {
      const bulkStudentsMapper = (
        (bulkOperation?.results || []) as MoveStudentsBulkOperationItem[]
      ).reduce(
        (acc, s) => {
          if (s.studentId) {
            acc[s.studentId] = s;
          }

          return acc;
        },
        {} as {
          [studentId: string]: MoveStudentsBulkOperationItem;
        }
      );

      onClose(
        studentsToProcess.filter(s =>
          s.userId in bulkStudentsMapper ? bulkStudentsMapper[s.userId].success : true
        )
      );
      return;
    }

    onClose([]);
  }, [onClose, students, bulkOperation, view]);

  const startProgress = (reason: string) => {
    if (!reason) {
      return;
    }

    const studentsToProcess = students || [];

    if (studentsToProcess.length === 0) {
      setView('successes');
      return;
    }

    setView('progress');
    setOperationId('');

    const startDelete = async () => {
      let operationId: string = '';

      try {
        const organizationId = user?.metadata?.organizationDetails?.organizationId || '';
        const result = await bulkService.deleteStudents(
          organizationId,
          studentsToProcess.map(s => s.userId),
          reason
        );
        operationId = result.data?.data?.operationId || '';
      } catch (e) {}

      setOperationId(operationId);
      if (!operationId) {
        setView('failure');
        setBulkOperation(undefined);
      }
    };
    startDelete();
  };

  const chooseReasonView = useMemo(() => {
    return (
      <ChooseReason
        students={students}
        onReasonSelected={(reason: string) => {
          setReason(reason);
          startProgress(reason);
        }}
        onClose={closePopup}
      />
    );
  }, [students, closePopup]);

  const successesView = useMemo(() => {
    return <Successes students={students} bulkOperation={bulkOperation} onClose={closePopup} />;
  }, [students, bulkOperation, closePopup]);

  const partialSuccessesView = useMemo(() => {
    return (
      <PartialSuccesses students={students} bulkOperation={bulkOperation} onClose={closePopup} />
    );
  }, [students, bulkOperation, closePopup]);

  const failureView = useMemo(() => {
    return (
      reason && (
        <Failure
          students={students}
          bulkOperation={bulkOperation}
          onClose={closePopup}
          onTryAgain={() => {
            startProgress(reason);
          }}
        />
      )
    );
  }, [students, reason, bulkOperation, closePopup]);

  const bulkProgressView = useMemo(() => {
    return (
      <BulkProgress
        operationId={operationId}
        onFinish={(bulkOperation: BulkOperationResponseData) => {
          setBulkOperation(bulkOperation);

          switch (bulkOperation?.state) {
            case BulkOperationState.Completed:
              setView('successes');
              break;
            case BulkOperationState.PartialSuccess:
              setView('partial_successes');
              break;
            case BulkOperationState.Failed:
              setView('failure');
              break;
          }
        }}
      />
    );
  }, [operationId]);

  const content = useMemo(() => {
    switch (view) {
      case 'choose_Reason':
        return chooseReasonView;
      case 'successes':
        return successesView;
      case 'partial_successes':
        return partialSuccessesView;
      case 'failure':
        return failureView;
      case 'progress':
        return bulkProgressView;
    }
  }, [view, chooseReasonView, successesView, partialSuccessesView, failureView, bulkProgressView]);

  return (
    <AppModal
      show={show}
      className={classNames.modal}
      modalContentClassName={classnames(classNames.modalContent, {
        [classNames.progressModalContent]: view === 'progress',
      })}
      type='fade'
      blockBodyScroll={true}
    >
      {content}
    </AppModal>
  );
};

export default DeleteStudentsPopup;
